import { Box, Button, Grid2, Paper, TextField, Typography } from "@mui/material";
import { Formik } from "formik";
import { enqueueSnackbar } from "notistack";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import * as Yup from "yup";
import { createProject } from "../../api/account";
import { useTranslationHook } from "../../hooks";
import { useFormStyles } from "../../hooks/useFormStyles";
import { setLoading } from "../../store/UI";
import { CreateProjectRequest } from "../../types/interfaces/project";

interface ProjectAddProps {
  accountId: number;
  onCancelAction?: () => void;
}

const ProjectAdd = ({ 
  accountId, 
  onCancelAction 
}: ProjectAddProps) => {
  const { t } = useTranslationHook();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formStyles = useFormStyles();

  const session = JSON.parse(localStorage.getItem("session") || "{}");
  const ownerId = session?.user?.id ?? session?.userId;
  
  const handleSubmit = async (values: any) => {
    dispatch(setLoading(true));

    const request: CreateProjectRequest = {
      accountId,
      name: values.name,
      description: values.description,
      lat: Number.parseFloat(values.lat),
      lng: Number.parseFloat(values.lng),
      ownerId: ownerId,
      ...(values.startDate ? { startDate: values.startDate } : {}),
      ...(values.endDate ? { endDate: values.endDate } : {}),
    };

    try {
      const response = await createProject(accountId, request);
      enqueueSnackbar(t("projects.created"), { variant: "success" });
      navigate(
        `/accounts/${accountId}/projects/${response.id}`,
        { state: { fromAdd: true } }
      );
    } catch (error: any) {
      const label = error?.response?.data?.label;
      enqueueSnackbar(
        t(label ? `back-end.${label}` : "back-end.unknown"),
        { variant: "error" }
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2, p: 2 }}>
        <Typography sx={{ mb: 2 }} variant="h4">
          {t("projects.add-project")}
        </Typography>
        <Formik
          initialValues={{
            name: "",
            description: "",
            lat: "",
            lng: "",
            startDate: "",
            endDate: "",
          }}
          onSubmit={(values) => {
            handleSubmit(values);
          }}
          validationSchema={Yup.object().shape({
            name: Yup.string().required(
              t("admin.projects.project-name-required")
            ),
            description: Yup.string().required(
              t("admin.projects.project-description-required")
            ),
            lat: Yup.number()
              .min(-90, t("admin.projects.project-valid-lat"))
              .max(90, t("admin.projects.project-valid-lat")),
            lng: Yup.number()
              .min(-180, t("admin.projects.project-valid-lng"))
              .max(180, t("admin.projects.project-valid-lng")),
          })}
        >
          {(props) => {
            const {
              values,
              touched,
              errors,
              handleChange,
              handleBlur,
              handleSubmit,
            } = props;

            return (
              <Box component="form" onSubmit={handleSubmit} sx={{ p: 1 }}>
                <Typography variant="h5" sx={{ mb: 2, mt: 1 }}>
                  {t("admin.projects.project-information")}
                </Typography>

                <Grid2 container spacing={3}>
                  <Grid2 size={12}>
                    {/*Name*/}
                    <TextField
                      fullWidth
                      required
                      id="name"
                      label={t("admin.projects.name")}
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(errors.name && touched.name)}
                      helperText={errors.name ? errors.name : ""}
                      sx={{...formStyles}}
                    />
                  </Grid2>

                  {/* LOCATION */}
                  <Grid2 size={6}>
                    <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 700, mb: 2 }}>
                      {t("projects.location")}
                    </Typography>

                    <Grid2 container spacing={2}>
                      <Grid2 size={6}>
                        <TextField
                          fullWidth
                          id="lat"
                          label={t("admin.projects.lat")}
                          value={values.lat}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!(errors.lat && touched.lat)}
                          helperText={touched.lat && errors.lat}
                          sx={formStyles}
                        />
                      </Grid2>

                      <Grid2 size={6}>
                        <TextField
                          fullWidth
                          id="lng"
                          label={t("admin.projects.lng")}
                          value={values.lng}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!(errors.lng && touched.lng)}
                          helperText={touched.lng && errors.lng}
                          sx={formStyles}
                        />
                      </Grid2>
                    </Grid2>
                  </Grid2>

                  {/* DATES */}
                  <Grid2 size={6}>
                    <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 700, mb: 2 }}>
                      {t("projects.dates")}
                    </Typography>

                    <Grid2 container spacing={2}>
                      <Grid2 size={6}>
                        <TextField
                          fullWidth
                          id="startDate"
                          type="date"
                          label={t("admin.projects.start-date")}
                          InputLabelProps={{ shrink: true }}
                          value={values.startDate}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!(errors.startDate && touched.startDate)}
                          helperText={touched.startDate && errors.startDate}
                          sx={formStyles}
                        />
                      </Grid2>

                      <Grid2 size={6}>
                        <TextField
                          fullWidth
                          id="endDate"
                          type="date"
                          label={t("admin.projects.end-date")}
                          InputLabelProps={{ shrink: true }}
                          value={values.endDate}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!(errors.endDate && touched.endDate)}
                          helperText={touched.endDate && errors.endDate}
                          sx={formStyles}
                        />
                      </Grid2>
                    </Grid2>
                  </Grid2>

                  <Grid2 size={12}>
                    {/*Description*/}
                    <TextField
                      fullWidth
                      required
                      multiline
                      rows={4}
                      id="description"
                      label={t("admin.projects.description")}
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(errors.description && touched.description)}
                      helperText={errors.description ? errors.description : ""}
                      sx={{...formStyles}}
                    />
                  </Grid2>
                </Grid2>

                {/* ACTIONS */}
                <Grid2
                  size={12}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    mt: 3,
                  }}
                >
                  <Button type="submit" variant="contained">
                    {t("admin.accounts.account-save")}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={onCancelAction}
                  >
                    {t("admin.accounts.account-cancel")}
                  </Button>
                </Grid2>
              </Box>
            );
          }}
        </Formik>
      </Paper>
    </Box>
  );
};

export default ProjectAdd;
